'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import PageHero from '@/components/ui/PageHero';

interface BlogPost {
  slug: string;
  category: string;
  title: string;
  excerpt: string;
  readTime: string;
  date: string;
  featured?: boolean;
  tags: string[];
}

const posts: BlogPost[] = [
  {
    slug: 'how-to-launch-taxi-app-india',
    category: 'Guide',
    title: 'How to Launch Your Own Taxi App in India (2025 Complete Guide)',
    excerpt: 'A step-by-step guide to launching a white-label taxi app in India — from choosing the right platform to onboarding drivers and acquiring your first customers.',
    readTime: '12 min read',
    date: 'May 2025',
    featured: true,
    tags: ['Taxi App', 'White Label', 'India'],
  },
  {
    slug: 'white-label-mobility-platform-guide',
    category: 'Deep Dive',
    title: 'White-Label vs Custom Mobility Platform: Which is Right for Your Business?',
    excerpt: 'Comparing the cost, time-to-market, and scalability of white-label SaaS platforms versus building a custom mobility solution from scratch.',
    readTime: '8 min read',
    date: 'April 2025',
    tags: ['White Label', 'SaaS', 'Platform'],
  },
  {
    slug: 'fleet-management-software-benefits',
    category: 'Fleet',
    title: '10 Ways Fleet Management Software Reduces Costs for Indian Operators',
    excerpt: 'From predictive maintenance to AI-powered routing, discover how modern fleet management software helps Indian operators cut costs and improve efficiency.',
    readTime: '7 min read',
    date: 'April 2025',
    tags: ['Fleet Management', 'Cost Reduction', 'AI'],
  },
  {
    slug: 'hyperlocal-delivery-business-guide',
    category: 'Delivery',
    title: 'Starting a Hyperlocal Delivery Business in Tier 2 Cities: A Practical Guide',
    excerpt: 'Tier 2 and Tier 3 cities in India represent a massive untapped opportunity for hyperlocal delivery. Here\'s how to launch and scale profitably.',
    readTime: '10 min read',
    date: 'March 2025',
    tags: ['Hyperlocal', 'Tier 2 Cities', 'Delivery'],
  },
  {
    slug: 'saas-vs-custom-mobility-platform',
    category: 'Technology',
    title: 'SaaS Mobility Platform vs Custom Development: A Cost Analysis for 2025',
    excerpt: 'A detailed cost breakdown comparing SaaS subscription models with custom app development for mobility businesses in India.',
    readTime: '9 min read',
    date: 'March 2025',
    tags: ['SaaS', 'Cost Analysis', 'Technology'],
  },
  {
    slug: 'food-delivery-platform-launch',
    category: 'Food Delivery',
    title: 'How to Launch a Branded Food Delivery Platform Without Building from Scratch',
    excerpt: 'Learn how restaurant owners and entrepreneurs are launching their own Swiggy/Zomato alternatives using white-label technology.',
    readTime: '6 min read',
    date: 'February 2025',
    tags: ['Food Delivery', 'White Label', 'Restaurant'],
  },
];

const categories = ['All', 'Guide', 'Deep Dive', 'Fleet', 'Delivery', 'Technology', 'Food Delivery'];

const blogListSchema = {
  '@context': 'https://schema.org',
  '@type': 'Blog',
  name: 'Movio Blog',
  description: 'Expert insights on fleet management, white-label mobility platforms, and delivery business growth.',
  url: `${process.env.NEXT_PUBLIC_SITE_URL || 'http://localhost:3000'}/blog`,
  publisher: {
    '@type': 'Organization',
    name: 'Movio',
    logo: {
      '@type': 'ImageObject',
      url: `${process.env.NEXT_PUBLIC_SITE_URL || 'http://localhost:3000'}/assets/images/app_logo.png`,
    },
  },
  blogPost: posts.map((post) => ({
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    url: `${process.env.NEXT_PUBLIC_SITE_URL || 'http://localhost:3000'}/blog/${post.slug}`,
    datePublished: post.date,
    author: { '@type': 'Organization', name: 'Movio' },
    keywords: post.tags.join(', '),
  })),
};

export default function BlogContent() {
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('is-visible')),
      { threshold: 0.05, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [activeCategory]);

  const filteredPosts =
    activeCategory === 'All' ? posts : posts.filter((p) => p.category === activeCategory);

  const featured = filteredPosts.find((p) => p.featured);
  const rest = filteredPosts.filter((p) => !p.featured);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(blogListSchema) }}
      />

      <section className="pt-24" aria-label="Blog">
        {/* Hero */}
        <PageHero className="py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="animate-on-scroll stagger-1">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/30 bg-white/15 text-[11px] font-semibold tracking-widest text-white uppercase mb-6">
                Insights & Guides
              </span>
              <h1 className="font-display text-5xl md:text-6xl font-semibold text-white tracking-tight leading-tight mb-6 mt-2">
                Mobility Business{' '}
                <span className="italic font-light text-white/80">Insights.</span>
              </h1>
              <p className="text-white/70 text-xl max-w-2xl mx-auto leading-relaxed animate-on-scroll stagger-2">
                Expert guides, case studies, and industry insights for mobility and delivery entrepreneurs across India.
              </p>
            </div>
          </div>
        </PageHero>

        {/* Category filter */}
        <div className="bg-white border-b border-[rgba(15,23,42,0.06)] sticky top-16 md:top-20 z-30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2 overflow-x-auto py-4 scrollbar-hide" role="tablist" aria-label="Blog categories">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium border transition-all ${
                    activeCategory === cat
                      ? 'bg-[#0066FF] border-[#0066FF] text-white'
                      : 'border-[rgba(0, 102, 255,0.2)] text-[#475569] hover:text-[#0066FF] hover:border-[#0066FF] hover:bg-[rgba(0, 102, 255,0.05)]'
                  }`}
                  role="tab"
                  aria-selected={activeCategory === cat}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-[#FFFFFF] py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Featured post */}
            {featured && (
              <article className="animate-on-scroll stagger-1 mb-12 bg-white rounded-2xl border border-[rgba(15,23,42,0.08)] shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                <div className="grid md:grid-cols-2 gap-0">
                  <div className="p-12 flex flex-col justify-center min-h-[300px]" style={{ background: 'linear-gradient(135deg, #075DDF 0%, #0066FF 60%, #3B82F6 100%)' }}>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 border border-white/30 text-white text-xs font-semibold mb-4 w-fit">
                      ⭐ Featured
                    </span>
                    <div className="text-white/70 text-xs font-semibold uppercase tracking-wider mb-3">{featured.category}</div>
                    <h2 className="font-display text-2xl md:text-3xl font-semibold text-white leading-tight mb-4">
                      {featured.title}
                    </h2>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {featured.tags.map((tag) => (
                        <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-white/[0.15] text-white/90">{tag}</span>
                      ))}
                    </div>
                  </div>
                  <div className="p-10 flex flex-col justify-between">
                    <div>
                      <p className="text-[#475569] leading-relaxed text-base mb-6">{featured.excerpt}</p>
                      <div className="flex items-center gap-4 text-sm text-[#64748B]">
                        <span className="flex items-center gap-1.5">
                          <Icon name="ClockIcon" size={14} />
                          {featured.readTime}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Icon name="CalendarDaysIcon" size={14} />
                          {featured.date}
                        </span>
                      </div>
                    </div>
                    <Link
                      href={`/blog/${featured.slug}`}
                      className="mt-6 inline-flex items-center gap-2 text-[#0066FF] font-semibold hover:gap-3 transition-all"
                      aria-label={`Read: ${featured.title}`}
                    >
                      Read Full Guide
                      <Icon name="ArrowRightIcon" size={16} />
                    </Link>
                  </div>
                </div>
              </article>
            )}

            {/* Empty state */}
            {filteredPosts.length === 0 && (
              <div className="text-center py-16">
                <p className="text-[#475569] text-base">No posts found in this category yet.</p>
              </div>
            )}

            {/* Post grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map((post, idx) => (
                <article
                  key={post.slug}
                  className={`animate-on-scroll stagger-${idx + 2} bg-white rounded-2xl border border-[rgba(15,23,42,0.08)] shadow-sm hover:shadow-md transition-all duration-300 flex flex-col overflow-hidden group`}
                >
                  <div className="p-8 flex-1 flex flex-col">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs font-semibold text-[#0066FF] uppercase tracking-wider">{post.category}</span>
                      <span className="text-xs text-[#64748B]">{post.date}</span>
                    </div>
                    <h2 className="font-display text-lg font-semibold text-[#0F172A] leading-snug mb-3 group-hover:text-[#0066FF] transition-colors">
                      {post.title}
                    </h2>
                    <p className="text-sm text-[#475569] leading-relaxed flex-1 mb-4">{post.excerpt}</p>
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {post.tags.map((tag) => (
                        <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-[rgba(0, 102, 255,0.08)] text-[#0066FF]">{tag}</span>
                      ))}
                    </div>
                  </div>
                  <div className="px-8 pb-6 flex items-center justify-between border-t border-[rgba(15,23,42,0.05)] pt-4">
                    <span className="flex items-center gap-1.5 text-xs text-[#64748B]">
                      <Icon name="ClockIcon" size={12} />
                      {post.readTime}
                    </span>
                    <Link
                      href={`/blog/${post.slug}`}
                      className="text-sm font-semibold text-[#0066FF] flex items-center gap-1 hover:gap-2 transition-all"
                      aria-label={`Read: ${post.title}`}
                    >
                      Read More
                      <Icon name="ArrowRightIcon" size={14} />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>

        {/* Newsletter CTA */}
        <section className="py-20 bg-white" aria-label="Newsletter signup">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="animate-on-scroll stagger-1">
              <h2 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A] mb-4">
                Get insights delivered to your inbox
              </h2>
              <p className="text-[#475569] text-lg mb-8">
                Join 500+ mobility entrepreneurs who read our weekly insights on fleet tech, delivery platforms, and business growth.
              </p>
              <Link
                href="/contact"
                className="btn-primary inline-flex items-center gap-2 px-8 py-4 rounded-xl text-base font-semibold"
              >
                Subscribe for Free
                <Icon name="ArrowRightIcon" size={18} />
              </Link>
            </div>
          </div>
        </section>
      </section>
    </>
  );
}